import { useEffect, useRef, useState } from 'react';

import { useFocusTrap } from '../../hooks/useFocusTrap.js';

/**
 * Generic over the row shape rather than pinned to ApprovalInboxEntry: the
 * dialog reads only `name`, and the cross-week triage list hands it a
 * (person, week) item instead. Keeping it generic lets the caller get its own
 * type back in `onSubmit` without a cast.
 */
interface ReturnDialogProps<T extends { name: string }> {
  entry: T | null;
  onClose: () => void;
  onSubmit: (entry: T, comment: string) => void;
  isPending: boolean;
  variant?: 'return' | 'unapprove';
}

export function ReturnDialog<T extends { name: string }>({
  entry,
  onClose,
  onSubmit,
  isPending,
  variant = 'return',
}: ReturnDialogProps<T>) {
  const [comment, setComment] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, Boolean(entry));

  useEffect(() => {
    if (!entry) {
      setComment('');
      return;
    }
    textareaRef.current?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [entry, onClose]);

  if (!entry) return null;

  const canSubmit = comment.trim().length > 0 && !isPending;
  const isUnapprove = variant === 'unapprove';
  const title = isUnapprove ? 'Unapprove timesheet' : 'Return for correction';
  const submitLabel = isUnapprove
    ? `Send and unapprove ${entry.name}'s timesheet`
    : `Send and return ${entry.name}'s timesheet for correction`;
  const submitText = isPending
    ? isUnapprove
      ? 'Unapproving…'
      : 'Returning…'
    : isUnapprove
      ? 'Unapprove'
      : 'Send & return';

  return (
    <div
      className="dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${variant}-dialog-title`}
        className="return-dialog"
      >
        <h2 id={`${variant}-dialog-title`} className="dialog-title">
          {title}
        </h2>
        <div className="dialog-body">
          <label htmlFor={`${variant}-comment`} className="dialog-label">
            Comment for {entry.name}
            <span className="required-indicator" aria-hidden="true">
              {' '}
              *
            </span>
            <span className="sr-only"> (required)</span>
          </label>
          <textarea
            ref={textareaRef}
            id={`${variant}-comment`}
            className="return-textarea"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            placeholder="Explain what needs to be corrected…"
            aria-required="true"
            aria-describedby={`${variant}-comment-hint`}
          />
          <p id={`${variant}-comment-hint`} className="dialog-hint">
            This comment will be visible to {entry.name} on their timesheet.
          </p>
        </div>
        <div className="dialog-footer">
          <button
            type="button"
            className="btn btn--destructive"
            onClick={() => onSubmit(entry, comment)}
            disabled={!canSubmit}
            aria-busy={isPending}
            aria-label={submitLabel}
          >
            {submitText}
          </button>
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
